// src/pages/Testcheck.jsx
import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import { useLocation, useNavigate } from "react-router-dom";
import * as Tone from "tone";
import { useTranslation } from 'react-i18next';

import { MicrophoneCheckDialog } from "./MicrophoneCheckDialog";

import "../styles/testCheck.css";

function Testcheck() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const mode = location.state?.mode ?? "diagnostic";

  const [volDialogOpen, setVolDialogOpen] = useState(false);
  const openVolumeDialog = () => setVolDialogOpen(true);
  const closeVolumeDialog = () => setVolDialogOpen(false);

  const confirmVolumeAndStart = () => {
    setVolDialogOpen(false);
    const nextRoute = mode === "screening" ? "/screening" : "/hearing";
    navigate(nextRoute, { state: { ...(location.state || {}), from: location.pathname } });
  };

  const [micDialogOpen, setMicDialogOpen] = useState(false);
  const [micStatus, setMicStatus] = useState("idle"); // idle | ok

  const openMicDialog = () => setMicDialogOpen(true);
  const closeMicDialog = () => setMicDialogOpen(false);

  const markMicComplete = () => {
    setMicStatus("ok");
    setMicDialogOpen(false);
  };

  const roomCheckPassed = micStatus === "ok";

  const [hpDialogOpen, setHpDialogOpen] = useState(false);
  const [hpStatus, setHpStatus] = useState("idle"); // idle | ok
  const [leftOk, setLeftOk] = useState(false);
  const [rightOk, setRightOk] = useState(false);

  const [leftPlayed, setLeftPlayed] = useState(false);
  const [rightPlayed, setRightPlayed] = useState(false);
  const [hpPlaying, setHpPlaying] = useState(false);
  const [hpAudioError, setHpAudioError] = useState("");

  const stopRef = useRef(() => { });

  async function playCheckTone(side /* 'left' | 'right' */) {
    setHpAudioError("");
    setHpPlaying(true);
    try {
      stopRef.current?.();
    } catch { }

    try {
      await Tone.start();

      const merge = new Tone.Merge();
      merge.toDestination();

      const gain = new Tone.Gain(0);
      const osc = new Tone.Oscillator({
        type: "sine",
        frequency: 500,
      });

      const inputIndex = side === "left" ? 0 : 1;
      gain.connect(merge, 0, inputIndex);
      osc.connect(gain);

      const now = Tone.now();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.75, now + 0.02);
      osc.start(now);
      osc.stop(now + 0.7);
      gain.gain.linearRampToValueAtTime(0, now + 0.68);

      const cleanup = () => {
        try {
          osc.stop();
        } catch { }
        try {
          osc.dispose();
        } catch { }
        try {
          gain.dispose();
        } catch { }
        try {
          merge.dispose();
        } catch { }
        setHpPlaying(false);
      };

      stopRef.current = cleanup;

      window.setTimeout(() => {
        if (side === "left") setLeftPlayed(true);
        if (side === "right") setRightPlayed(true);
        cleanup();
      }, 800);
    } catch (e) {
      setHpPlaying(false);
      setHpAudioError(e?.message || t('testcheck.audioFailed'));
    }
  }

  const openHpDialog = () => {
    if (!roomCheckPassed) return;

    setLeftOk(false);
    setRightOk(false);
    setLeftPlayed(false);
    setRightPlayed(false);
    setHpAudioError("");
    setHpDialogOpen(true);
  };

  const closeHpDialog = () => {
    try {
      stopRef.current?.();
    } catch { }
    setHpDialogOpen(false);
  };

  const markHeadphonesComplete = () => {
    setHpStatus("ok");
    setHpDialogOpen(false);
  };

  const headphonesReady = hpStatus === "ok";
  const canProceed = roomCheckPassed && headphonesReady;

  const statusChip = (status) => {
    const label = status === "ok" ? t('common.ok') : t('common.notChecked');
    const color = status === "ok" ? "success" : "default";
    const variant = status === "idle" ? "outlined" : "filled";
    return <Chip size="small" label={label} color={color} variant={variant} />;
  };

  const canMarkComplete = leftPlayed && rightPlayed && leftOk && rightOk && !hpPlaying;

  return (
    <Box className="testcheck">
      <Paper elevation={3} className="testcheck__paper">
        <Typography component="h1" variant="h6" align="center" className="testcheck__title">
          {t('testcheck.title')}
        </Typography>

        <Typography variant="subtitle2" className="testcheck__stepTitle">
          {t('testcheck.step1')}
        </Typography>

        <Box className="testcheck__row">
          <Box className="testcheck__rowLeft">
            <GraphicEqIcon fontSize="medium" />
            <Typography>{t('testcheck.microphoneCheck')}</Typography>
          </Box>

          <Box className="testcheck__rowRight">
            <Chip
              size="small"
              label={roomCheckPassed ? t('common.ok') : t('common.notChecked')}
              color={roomCheckPassed ? "success" : "default"}
              variant={roomCheckPassed ? "filled" : "outlined"}
            />
            <Button
              variant="outlined"
              onClick={openMicDialog}
              disabled={micStatus === "ok"}
              data-cy="check_microphone_btn"
            >
              {micStatus === "ok" ? t('testcheck.checked') : t('testcheck.checkMicrophone')}
            </Button>
          </Box>
        </Box>

        {!roomCheckPassed && (
          <Alert severity="info" className="testcheck__infoAlert">
            {t('testcheck.unlockHeadphoneCheck')}
          </Alert>
        )}

        <Divider className="testcheck__divider" />

        <Typography
          variant="subtitle2"
          className={`testcheck__stepTitle ${roomCheckPassed ? '' : 'is-disabledText'}`}
        >
          {t('testcheck.step2')}
        </Typography>

        <Box className={`testcheck__row ${roomCheckPassed ? '' : 'is-disabledBlock'}`}>
          <Box className="testcheck__rowLeft">
            <HeadphonesIcon fontSize="medium" />
            <Typography>{t('testcheck.headphonesCheck')}</Typography>
          </Box>

          <Box className="testcheck__rowRight">
            {statusChip(hpStatus)}
            <Button variant="outlined" onClick={openHpDialog} disabled={!roomCheckPassed} data-cy="check_headphones_btn">
              {t('testcheck.checkHeadphones')}
            </Button>
          </Box>
        </Box>

        <Box className="testcheck__actionRow">
          <Button
            onClick={() => navigate('/assigntest', { state: { ...(location.state || {}), from: location.pathname } })}
            variant="contained"
            className="testcheck__backBtn"
            data-cy="back_btn"
          >
            {t('common.back')}
          </Button>
          <Button
            onClick={openVolumeDialog}
            variant="contained"
            className="testcheck__proceedBtn"
            disabled={!canProceed}
            data-cy="proceed_btn"
          >
            {t('common.proceed')}
          </Button>
        </Box>

        {!headphonesReady && roomCheckPassed && (
          <Typography variant="body2" align="center" className="testcheck__hint">
            {t('testcheck.finishHeadphoneCheck')}
          </Typography>
        )}
      </Paper>

      <MicrophoneCheckDialog
        open={micDialogOpen}
        onClose={closeMicDialog}
        onPassed={markMicComplete}
        scanSeconds={10}
        sustainFailSeconds={2}
        splOffsetDb={69.2}
      />

      <Dialog open={hpDialogOpen} onClose={closeHpDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('testcheck.headphonesDialogTitle')}</DialogTitle>
        <DialogContent className="testcheck__dialogContent">
          <Typography className="testcheck__dialogIntro">{t('testcheck.headphonesDialogBody')}</Typography>

          {hpAudioError && (
            <Alert severity="error" className="testcheck__errorAlert">
              {hpAudioError}
            </Alert>
          )}

          <Box className="testcheck__grid2">
            <Box className="testcheck__earCard">
              <Typography variant="subtitle2" className="testcheck__earTitle">{t('testcheck.leftSide')}</Typography>

              <Button
                variant="outlined"
                onClick={() => playCheckTone('left')}
                disabled={hpPlaying}
                fullWidth
              >
                {hpPlaying ? 'Playing...' : (leftPlayed ? 'Replay left tone' : 'Play left tone to proceed')}
              </Button>

              <FormControlLabel
                className="testcheck__earCheck"
                control={
                  <Checkbox checked={leftOk} onChange={(e) => setLeftOk(e.target.checked)} disabled={!leftPlayed} />
                }
                label={leftPlayed ? t('testcheck.leftWorks') : t('testcheck.playLeft')}
              />
            </Box>

            <Box className="testcheck__earCard">
              <Typography variant="subtitle2" className="testcheck__earTitle">{t('testcheck.rightSide')}</Typography>

              <Button variant="outlined" onClick={() => playCheckTone("right")} disabled={hpPlaying} fullWidth>
                {hpPlaying ? t('testcheck.playing') : rightPlayed ? t('testcheck.replayRight') : t('testcheck.playRight')}
              </Button>

              <FormControlLabel
                className="testcheck__earCheck"
                control={
                  <Checkbox checked={rightOk} onChange={(e) => setRightOk(e.target.checked)} disabled={!rightPlayed} />
                }
                label={rightPlayed ? t('testcheck.rightWorks') : t('testcheck.playRight')}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeHpDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={markHeadphonesComplete}
            disabled={!canMarkComplete}
            data-cy="mark_headphones_complete_btn"
          >
            {t('testcheck.markComplete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={volDialogOpen} onClose={closeVolumeDialog}>
        <DialogTitle>{t('testcheck.volumeTitle')}</DialogTitle>
        <DialogContent className="testcheck__dialogContent">
          {t('testcheck.volumeBody')}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVolumeDialog} data-cy="cancel_btn">
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={confirmVolumeAndStart} data-cy="set_volume_to_100_btn">
            {t('testcheck.setTo100')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Testcheck;